import { AddOrEditMatterIntakeTemplateModal } from "@/components/features/settings/defaults/matter-intake-templates/AddOrEditMatterIntakeTemplateModal";
import { MatterIntakeTemplateRowContextMenu } from "@/components/features/settings/defaults/matter-intake-templates/MatterIntakeTemplateRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import { ActionConfirmationModal, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterIntakeTemplate,
  useDeleteMatterIntakeTemplate,
} from "@/lib/data/react-query/matters/matter-intake-templates";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type MatterIntakeTemplate } from "@/lib/types";
import { customDateTimeSortingFns } from "@/lib/utils/tableUtils";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const MATTER_INTAKE_TEMPLATE_TABLE_COLUMN_IDS = [
  "Label",
  "Description",
] as const;

const MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY: string =
  tanstackTableNames.matterIntakeTemplates;

function MatterIntakeTemplatesTableDangerous({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();
  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplate,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIntakeTemplate,
    ACTION_PERMISSION_VALUES.delete,
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [templateToDelete, setTemplateToDelete] =
    useState<MatterIntakeTemplate | null>(null);
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const createMatterIntakeTemplate = useCreateMatterIntakeTemplate();
  const deleteMatterIntakeTemplate = useDeleteMatterIntakeTemplate();

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY);
    const ids = [...MATTER_INTAKE_TEMPLATE_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(
        MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY,
      );
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const {
    data: matterSettingsData,
    isLoading,
    isError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const matterIntakeTemplates = useMemo(
    () => matterSettingsData?.data.intakeTemplates ?? [],
    [matterSettingsData?.data.intakeTemplates],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return matterIntakeTemplates;
    return matterIntakeTemplates.filter((row) => {
      const label = row.label.toLowerCase();
      const description = (row.description ?? "").toLowerCase();
      return label.includes(q) || description.includes(q);
    });
  }, [matterIntakeTemplates, searchQuery]);

  const columns = useMemo<ColumnDef<MatterIntakeTemplate>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => row.original.label,
        sortingFn: "alphanumeric",
      },
      {
        id: "Description",
        accessorFn: (row) => row.description ?? "",
        header: "Description",
        cell: ({ row }) => row.original.description ?? "-",
        sortingFn: "alphanumeric",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: customDateTimeSortingFns,
  });

  const sensors = useMattersStyleTableColumnDragSensors();

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_INTAKE_TEMPLATES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const selectedTemplate = matterIntakeTemplates.find(
    (template) => template.id === selectedTemplateId,
  );

  useEffect(() => {
    if (createMatterIntakeTemplate.isSuccess) {
      setIsEditing(true);
    }
  }, [createMatterIntakeTemplate.isSuccess]);

  useEffect(() => {
    if (isAdding) {
      setIsEditing(false);
      setSelectedTemplateId(null);
      setIsSlideoverOpen(true);
    }
  }, [isAdding]);

  const handleSlideoverOpenChange = (open: boolean) => {
    setIsSlideoverOpen(open);
    if (!open) {
      setIsEditing(false);
      setSelectedTemplateId(null);
      setIsAdding(false);
    }
  };

  const handleEditClick = (template: MatterIntakeTemplate) => {
    setIsEditing(true);
    setSelectedTemplateId(template.id);
    setIsSlideoverOpen(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    await deleteMatterIntakeTemplate.mutateAsync(templateToDelete.id);
    setTemplateToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={MATTER_INTAKE_TEMPLATE_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search templates...",
        }}
        isLoading={isLoading}
        isError={isError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No intake templates found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="templates"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterIntakeTemplates,
          "data-table-name": tanstackTableNames.matterIntakeTemplates,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            handleEditClick(row.original);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterIntakeTemplateRowContextMenu
            template={row.original}
            trigger={trigger}
            canEdit={hasEditPermission}
            canDelete={hasDeletePermission}
            onEdit={() => {
              handleEditClick(row.original);
            }}
            onDelete={() => {
              setTemplateToDelete(row.original);
            }}
          />
        )}
      />

      <AddOrEditMatterIntakeTemplateModal
        editing={isEditing}
        open={isSlideoverOpen}
        setOpen={handleSlideoverOpenChange}
        setEditing={setIsEditing}
        setSelectedTemplateId={setSelectedTemplateId}
        template={selectedTemplate}
        isReadOnly={isEditing && (!hasEditPermission || !hasDeletePermission)}
      />

      <ActionConfirmationModal
        open={Boolean(templateToDelete)}
        setOpen={(open) => {
          if (!open) setTemplateToDelete(null);
        }}
        title="Delete intake template"
        description={`Are you sure you want to delete "${templateToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteTemplate}
      />
    </div>
  );
}

export function MatterIntakeTemplatesTable({
  isAdding,
  setIsAdding,
}: {
  isAdding: boolean;
  setIsAdding: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterIntakeTemplatesTableDangerous
        isAdding={isAdding}
        setIsAdding={setIsAdding}
      />
    </ErrorBoundary>
  );
}
